/**
 * @file Common photo manipulation utils.
 */
import get from 'lodash.get';
import * as EXIF from 'exif-js';

import { IMAGE_SIZE_CODE } from '../constants/image-size-codes';
import { ALBUM_FOLDER_TAG_TYPE } from '../constants/album-folder-tags';

/**
 * Accepts min or max as a param and returns a function
 * that returns the biggest or smallest (in area)
 * out of an array of thumbnails of the followin example.
 *
 * @example
 * [{
 *  url: String,
 *  width: Number,
 *  height: Number,
 * }]
 *
 * @param {string} size - Size "min" or "max".
 * @returns {Function}
 */
const getThumbSize = size => thumbs => {
  if (!thumbs) {
    return '';
  }

  const start = size === 'max' ? 0 : Infinity;
  const area = thumbs.reduce(
    (acc, t) => Math[size](acc, t.width * t.height),
    start,
  );

  return thumbs.find(t => area === t.width * t.height);
};

export const getMinThumb = getThumbSize('min');

export const getMaxThumb = getThumbSize('max');

/**
 * Gets a thumbnail by size.
 *
 * @param {Array<Object>} thumbnails - The image thumbnails.
 * @param {IMAGE_SIZE_CODE} size - The preferred size.
 * @param {Boolean} [fallback] - Whether to try to get any available size if that requested one is not found.
 * @returns {<Object>|undefined} - The thumbnail of the preferred size or the fallback one.
 */
export const getThumbBySize = (thumbnails, size, fallback) => {
  if (!thumbnails) {
    return undefined;
  }

  if (!Object.values(IMAGE_SIZE_CODE).includes(size)) {
    throw Error(`getThumbBySize(): Size is not acceptable, please select
      a size from the IMAGE_SIZE_CODE enum`);
  }

  const thumbnail = thumbnails.find(t => t.size_code === size);
  const fallbackThumbnail = thumbnails[thumbnails.length - 1];

  if (thumbnail) {
    return thumbnail;
  }

  if ((fallback || thumbnails?.length === 1) && fallbackThumbnail) {
    return fallbackThumbnail;
  }

  return undefined;
};

/**
 * Given an array of thumbnail_urls returns the src and
 * srcSet strings that photos can use to display properly.
 *
 * @param {Array} thumbs The photo thumbnails.
 * @param {string} [maxSize] String from IMAGE_SIZE_CODE which states the max size of sources.
 * @param {string} [minSize] String from IMAGE_SIZE_CODE which states the min size of sources.
 * @returns {{src: string, srcSet: (*|string)}}
 */
export const getImageSources = (thumbs, maxSize, minSize) => {
  if (!thumbs || !Array.isArray(thumbs) || thumbs.length === 0) {
    return {
      src: '',
      srcSet: undefined,
    };
  }

  let maxIndex = thumbs.length - 1;
  if (maxSize) {
    // Find the index of the max size and go down from there.
    const maxSizeThumb = thumbs.find(thumb => thumb.size_code === maxSize);
    if (maxSizeThumb) {
      maxIndex = thumbs.indexOf(maxSizeThumb);
    }
  }

  let minIndex = 0;
  if (minSize) {
    // Find the index of the min size and go up from there.
    const minSizeThumb = thumbs.find(thumb => thumb.size_code === minSize);
    if (minSizeThumb) {
      minIndex = thumbs.indexOf(minSizeThumb);
    }
  }

  if (minIndex > maxIndex) {
    throw Error('Min size cannot be larger than Max Size');
  }

  const src = thumbs[maxIndex].url;
  const srcSet =
    thumbs &&
    thumbs
      .slice(minIndex, maxIndex + 1)
      .map(({ url, width }) => `${url} ${width}w`)
      .join(', ');

  return { src, srcSet };
};

/**
 * Produces an image's ratio provided its thumbnails by
 * looking to find the first thumbnail that has both a
 * width and a height and then calculating the ratio.
 *
 * @param {array} thumbnails The image thumbnails.
 * @returns {number|undefined} The ratio or undefined if not found.
 */
export const getImageRatioFromThumbnails = (thumbnails = []) => {
  if (!thumbnails) {
    return undefined;
  }

  const thumbnail = thumbnails.find(t => t.width && t.height);

  if (!thumbnail) {
    return undefined;
  }

  return thumbnail.width / thumbnail.height;
};

/**
 * Flattens facebox records of the following form.
 *
 * @example Array[Object({facebox})]
 *
 * @param {Array} records Array of facebox records.
 * @returns {Array} - A two dimensional array with facebox records.
 */
export const flattenFaceboxRecords = records => {
  return records.reduce((acc, record) => {
    acc.push(record.facebox);
    return acc;
  }, []);
};

/**
 * Given an array of facebox records in the form of Array[Object({facebox})]
 * it returns the biggest ones based on their calculated area (width * height).
 *
 * @param {Array<Object>|Array<Array>} records - Facebox records.
 * @param {Number} limit - How many faceboxes to return.
 * @returns {Array.<Array>}
 */
export const getLargestFaceboxes = (records, limit) => {
  const faceboxes = Array.isArray(records)
    ? records
    : flattenFaceboxRecords(records);

  return faceboxes
    .sort((a, b) => {
      /* eslint-disable no-unused-vars */
      const [xA, yA, widthA, heightA] = a;
      const [xB, yB, widthB, heightB] = b;
      /* eslint-enable no-unused-vars */
      const areaA = widthA * heightA;
      const areaB = widthB * heightB;

      if (areaA > areaB) {
        return -1;
      }

      if (areaA < areaB) {
        return 1;
      }

      return 0;
    })
    .slice(0, limit || faceboxes.length);
};

/**
 * Calculates required values for photo obfuscation.
 *
 * @param {Array} facebox
 * @param {number} [multiplier=1] A factor by which to decrease or increase the
 * obfuscation size (e.g. 0.5 or 1.5).
 * @param {number} [minSizeThreshold=0] A minimum area (size) where if the total facebox area is lower than it we should fallback to `minFallbackSize`.
 * @param {number} [minFallbackSize=0.15] A fallback minimum area size we should fallback to if the facebox's original area is less than `minSizeThreshold`.
 * @param {number} [maxSize=1] The maximum area size (in percentage) that the faceblocker can cover.
 *
 * @returns {{
 * centerX: number,
 * centerY: number,
 * blockerX: number,
 * blockerY: number,
 * blockerSize: number,
 * blockerSvgRadius: number
 * }}
 */
export const blockerDataFromFacebox = (
  facebox,
  multiplier = 1,
  minSizeThreshold = 0,
  minFallbackSize = 0.15,
  maxSize = 1,
) => {
  const [x, y, width, height] = facebox;
  const minDimension = Math.min(width, height);
  const widthActual =
    minDimension <= minSizeThreshold && multiplier > 0
      ? Math.min(minFallbackSize, maxSize)
      : width * multiplier;
  const heightActual =
    minDimension <= minSizeThreshold && multiplier > 0
      ? minFallbackSize
      : height * multiplier;

  // The size (width, height) of the blocker image
  const blockerSize = Math.max(
    Math.min(widthActual, maxSize),
    Math.min(heightActual, maxSize),
  );

  // The facebox's center on the X axis
  const centerX = width / 2 + x;

  // The facebox's center on the Y axis
  const centerY = height / 2 + y;

  // We're sizing the obfuscator (the Waldo logo) based on the max
  // facebox dimension so we need to offset its X or Y position accordingly
  // to the dimension we're changing (width or height respectively)
  // The below points are the top left corner of the obfucscator (the blocker image)
  const blockerX = x - (blockerSize - width) / 2;
  const blockerY = y - (blockerSize - height) / 2;

  return {
    centerX,
    centerY,
    blockerX,
    blockerY,
    blockerSize,
    // Where does `1.41` come from when calculating the SVG radius length?
    // It comes from how SVG's <circle> element determines its radius length
    // when the radius is provided in percentage. More info here:
    // https://oreillymedia.github.io/Using_SVG/extras/ch05-percentages.html
    //
    // The extra 10% in size (* 1.1) is just arbitrary to make the
    // clear face a bit larger.
    blockerSvgRadius: (blockerSize / 2 / 1.41) * 1.1,
  };
};

/**
 * Gets the watermarked or non-watermarked MEDIUM2X version of a
 * photo depending on whether the album allows it or not.
 *
 * @param {Object} photo The photo.
 * @param {Object} album The album.
 * @param {boolean} downloadWithWatermark Whether to get the watermarked version or not.
 * @returns {string} The url of the photo.
 */
export const getLowResPhotoUrl = (photo, album, downloadWithWatermark) => {
  if (!photo || !album) {
    return '';
  }

  if (downloadWithWatermark && album.watermark_config) {
    const urls = get(photo, 'watermarked_urls');
    return get(getThumbBySize(urls, IMAGE_SIZE_CODE.MEDIUM2X), 'url', '');
  }

  const urls = get(photo, 'thumbnail_urls');
  return get(getThumbBySize(urls, IMAGE_SIZE_CODE.MEDIUM2X), 'url', '');
};

export const getHiResPhotoUrl = (photo, album, downloadWithWatermark) => {
  if (downloadWithWatermark && album.watermark_config) {
    return getMaxThumb(photo.watermarked_urls).url;
  }

  return getMaxThumb(photo.thumbnail_urls).url;
};

/**
 * Computes the delete/create plan for the uniform team tag selections of a
 * photo. Only tags present as keys of `teamTagSelections` (the uniform tags
 * the UI manages) are ever touched — uniform records with no team involvement
 * may be matching-created and must be left alone.
 *
 * There is no per-team delete on the API: removing a team means deleting the
 * whole AlbumPhotoAlbumFolderTag record and re-creating it with the remaining
 * selection (creates are idempotent get-or-creates, so purely additive
 * changes never delete).
 *
 * @param {Object} options - The method's options.
 * @param {Array<Object>} [options.albumPhotoAlbumFolderTags] - The existing tags on the photo.
 * @param {Object} [options.teamTagSelections] - Map of AlbumFolderTag id to the selected team ids.
 * @returns {Object} { deleteRecordIds, createTagIds, albumFolderTagTeams }
 */
export const getUniformTeamTagChanges = ({
  albumPhotoAlbumFolderTags = [],
  teamTagSelections = {},
}) => {
  const uniformRecordsByTagId = {};
  albumPhotoAlbumFolderTags
    .filter(
      record => record.album_folder_tag.type === ALBUM_FOLDER_TAG_TYPE.UNIFORM,
    )
    .forEach(record => {
      uniformRecordsByTagId[record.album_folder_tag.id] = record;
    });

  const deleteRecordIds = [];
  const createTagIds = [];
  const albumFolderTagTeams = {};

  Object.entries(teamTagSelections).forEach(([tagId, selectedTeamIds]) => {
    const record = uniformRecordsByTagId[tagId];
    const existingTeamIds = (record?.teams?.records ?? []).map(
      ({ team }) => team.id,
    );
    const removedTeam = existingTeamIds.some(
      id => !selectedTeamIds.includes(id),
    );
    const addedTeam = selectedTeamIds.some(id => !existingTeamIds.includes(id));

    if (!removedTeam && !addedTeam) {
      return;
    }

    if (removedTeam && record) {
      // Recreating with the remaining teams (possibly none) keeps the
      // uniform tag itself on the photo.
      deleteRecordIds.push(record.id);
    }

    createTagIds.push(tagId);
    albumFolderTagTeams[tagId] = selectedTeamIds;
  });

  return { deleteRecordIds, createTagIds, albumFolderTagTeams };
};

/**
 * "Updates" custom AlbumPhotoAlbumFolderTags by replacing them, and the
 * photo's uniform team tags per `getUniformTeamTagChanges`.
 *
 * @param {Object} options - The method's options.
 * @param {String} options.albumId - The album id.
 * @param {Array<String>} options.photoIds - List of Photo Ids to add tags to.
 * @param {Array<Object>} options.albumPhotoAlbumFolderTags - The existing tags on the photo.
 * @param {Array<String>} [options.customTagIds] - The Custom AlbumFolderTag ids to be updated.
 * @param {Object} [options.teamTagSelections] - Map of uniform AlbumFolderTag id to the selected team ids.
 * @param {Function} options.createTags - The create tags mutation.
 * @param {Function} options.deleteTags - The delete tags mutation.
 * @param {Bool} options.photoGroupId PhotoGroup where the photos reside in.
 * @param {Bool} options.isSelectAll Whether or not to select all photos.
 * @param {String} options.albumPhotoFilter Current album photo filter. Ensures we are sticking to only the current photo selection.
 * @param {String} options.uploaderIds Current filter for uploaderIds.
 * @param {String} options.filenameSearchQuery Current filename filter
 * @param {String} options.objectSearchQuery Current object search filter
 *
 * @returns {Promise<undefined>}
 */
export const updateCustomAlbumPhotoAlbumFolderTags = async ({
  albumId,
  photoIds,
  albumPhotoAlbumFolderTags,
  customTagIds = [],
  teamTagSelections = {},
  createTags,
  deleteTags,
  photoGroupId,
  isSelectAll = false,
  albumPhotoFilter = undefined,
  uploaderIds = [],
  filenameSearchQuery = undefined,
  objectSearchQuery = undefined,
}) => {
  const customTagRecords = albumPhotoAlbumFolderTags.filter(
    albumPhotoAlbumFolderTag =>
      albumPhotoAlbumFolderTag.album_folder_tag.type ===
      ALBUM_FOLDER_TAG_TYPE.CUSTOM,
  );
  const albumPhotoAlbumFolderTagIds = customTagRecords.map(
    tag => tag.album_folder_tag.id,
  );
  const tagsEqual =
    customTagIds.every(id => albumPhotoAlbumFolderTagIds.includes(id)) &&
    customTagRecords.length === customTagIds.length;
  const skipCustom = customTagIds.length > 0 && tagsEqual;

  // Custom tags follow a replace-all strategy: delete every custom record,
  // then re-create the full selection.
  const customDeleteIds =
    !skipCustom && albumPhotoAlbumFolderTags.length > 0
      ? customTagRecords.map(({ id }) => id)
      : [];
  const customCreateIds = !skipCustom ? customTagIds : [];

  const { deleteRecordIds, createTagIds, albumFolderTagTeams } =
    getUniformTeamTagChanges({ albumPhotoAlbumFolderTags, teamTagSelections });

  const deleteIds = [...customDeleteIds, ...deleteRecordIds];
  const createIds = [...customCreateIds, ...createTagIds];

  if (deleteIds.length > 0 && deleteTags) {
    await deleteTags(deleteIds);
  }

  if (createIds.length > 0) {
    await createTags({
      albumId,
      albumFolderTagIds: createIds,
      ...(Object.keys(albumFolderTagTeams).length
        ? { albumFolderTagTeams }
        : {}),
      photo_ids: {
        select_all: isSelectAll,
        selected_ids: isSelectAll ? [] : photoIds,
        ignored_ids: isSelectAll ? photoIds : [],
      },
      album_photo_filter: albumPhotoFilter,
      uploader_ids: uploaderIds,
      original_filename: filenameSearchQuery,
      search_query: objectSearchQuery,
      album_photo_group_ids: photoGroupId ? [photoGroupId] : [],
      ungrouped_only: !photoGroupId,
    });
  }
};

/**
 * Resets orientation of a file based on the exit info.
 * Rotation concept found at https://stackoverflow.com/questions/20600800/js-client-side-exif-orientation-rotate-and-mirror-jpeg-images.
 *
 * @param {File} file Image file to rotate.
 */
export const fixFileOrientation = async file => {
  return new Promise(resolve => {
    const dataUrlReader = new FileReader();

    dataUrlReader.onload = readerEvent => {
      const fileUrl = readerEvent.target.result;
      const imageItem = new Image();

      imageItem.onload = () => {
        EXIF.getData(imageItem, function () {
          const srcOrientation = EXIF.getTag(this, 'Orientation') || 1;

          if (srcOrientation === 1) {
            resolve(fileUrl);
            return;
          }

          const { width, height } = imageItem;
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // set proper canvas dimensions before transform & export
          if (srcOrientation > 4 && srcOrientation < 9) {
            canvas.width = height;
            canvas.height = width;
          } else {
            canvas.width = width;
            canvas.height = height;
          }

          // transform context before drawing image
          switch (srcOrientation) {
            case 2:
              ctx.transform(-1, 0, 0, 1, width, 0);
              break;
            case 3:
              ctx.transform(-1, 0, 0, -1, width, height);
              break;
            case 4:
              ctx.transform(1, 0, 0, -1, 0, height);
              break;
            case 5:
              ctx.transform(0, 1, 1, 0, 0, 0);
              break;
            case 6:
              ctx.transform(0, 1, -1, 0, height, 0);
              break;
            case 7:
              ctx.transform(0, -1, -1, 0, height, width);
              break;
            case 8:
              ctx.transform(0, -1, 1, 0, 0, width);
              break;
            default:
              break;
          }

          // draw image
          ctx.drawImage(imageItem, 0, 0);

          // export base64
          resolve(canvas.toDataURL());
        });
      };

      imageItem.src = fileUrl;
    };

    dataUrlReader.readAsDataURL(file);
  });
};

/**
 *  Returns the computed data for styling a facebox outline item.
 *
 * @param {Array<Number>} opts Object containing all options.
 * @param {Array<Number>} opts.facebox The Facebox array to draw.
 * @param {Object} opts.photo Photo to to mark a facebox into.
 * @param {enum<IMAGE_SIZE_CODE>} opts.imageSize Image size of the photo.
 * @returns {{outlineWidth: number, top: number, left: number, outlineHeight: number}|null}
 */
export const getFaceboxOutlineData = ({ facebox, photo, imageSize }) => {
  if (!facebox || !photo || !imageSize) {
    return null;
  }

  const [x, y, w, h] = facebox;

  const thumbnails = photo?.thumbnail_urls;
  const photoSize = getThumbBySize(thumbnails, imageSize);

  const { width, height } = photoSize;

  const imageWidthScale = Math.min(1, width / height);
  const imageHeightScale = Math.min(1, height / width);
  const outlineWidth = imageWidthScale * w * 100;
  const outlineHeight = imageHeightScale * h * 100;
  const left = (1 - imageWidthScale) * 50 + imageWidthScale * x * 100;
  const top = (1 - imageHeightScale) * 50 + imageHeightScale * y * 100;

  return {
    w: outlineWidth / 100,
    h: outlineHeight / 100,
    outlineWidth,
    outlineHeight,
    left,
    top,
  };
};

/**
 *  Returns the computed style for a facebox outline item.
 *
 * @param {Array<Number>} opts Object containing all options.
 * @param {Array<Number>} opts.facebox The Facebox array to draw.
 * @param {Object} opts.photo Photo to to mark a facebox into.
 * @param {enum<IMAGE_SIZE_CODE>} opts.imageSize Image size of the photo.
 * @returns {Object|null} The style to apply to the facebox.  Returns undefined if no facebox or valid photo+imageSize is passed.
 */
export const getFaceboxOutlineStyle = ({ facebox, photo, imageSize }) => {
  if (!facebox) {
    return null;
  }

  const [x, y, w, h] = facebox;

  if (facebox && !photo) {
    return {
      width: `${w * 100}%`,
      height: `${h * 100}%`,
      top: `${y * 100}%`,
      left: `${x * 100}%`,
    };
  }

  const thumbnails = photo?.thumbnail_urls;
  const photoSize = getThumbBySize(thumbnails, imageSize);

  if (!photoSize) {
    return null;
  }

  const { outlineWidth, outlineHeight, top, left } = getFaceboxOutlineData({
    facebox,
    photo,
    imageSize,
  });

  return {
    width: `${outlineWidth}%`,
    height: `${outlineHeight}%`,
    top: `${top}%`,
    left: `${left}%`,
    transform: 'scale(1.6)',
  };
};

/**
 *  Returns the computed style for a facebox outline item
 *  based on DOM elements.
 *
 * @param {Array<Number>} opts Object containing all options.
 * @param {Array<Number>} opts.imageElement The img dom element of the photo.
 * @param {Object} opts.wrapperElement The image wrapper element.
 * @returns {Object|null} The style to apply to the facebox.  Returns undefined if no facebox or valid photo+imageSize is passed.
 */
export const getFaceboxOutlineStyleFromDOM = ({
  facebox,
  imageElement,
  wrapperElement,
}) => {
  if (!facebox || !imageElement || !wrapperElement) {
    return null;
  }

  const [x, y, w, h] = facebox;

  // Use the elements as reference:
  const width = imageElement.offsetWidth;
  const height = imageElement.offsetHeight;

  if (!width || !height) {
    return {};
  }

  const outlineWidth = w * width;
  const outlineHeight = h * height;
  const left = x * width;
  const top = y * height;
  // Extra height given by the wrapper container
  const extraWidth = wrapperElement.offsetWidth - width;
  const extraHeight = wrapperElement?.offsetHeight - height;

  return {
    width: `${outlineWidth}px`,
    height: `${outlineHeight}px`,
    top: `calc(${top}px + ${extraHeight / 2}px)`,
    left: `calc(${left}px + ${extraWidth / 2}px)`,
    transform: 'scale(1.1)',
  };
};

export const shouldUseForegroundThumbnails = photo => {
  if (!photo?.original_url || !photo?.foreground) {
    return false;
  }

  if (photo?.original_url?.mime_type !== 'image/png') {
    return false;
  }

  return photo?.foreground?.thumbnail_urls?.length > 0;
};
